import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePosthog } from "@/hooks/usePosthog";
import { useTranslation } from "react-i18next";

interface DomainSuggestionPopupProps {
  domain: string;
  currentEmail?: string;
  onConfirm: (domain: string) => void;
  onCancel: () => void;
}

const splitEmail = (email: string) => {
  const at = email.indexOf("@");
  if (at === -1) return { local: email, domain: "" };
  return { local: email.slice(0, at), domain: email.slice(at + 1) };
};

const EmailDomainPopup: React.FC<DomainSuggestionPopupProps> = ({
  domain,
  currentEmail = "",
  onConfirm,
  onCancel,
}) => {
  const { trackEvent } = usePosthog();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const { local, domain: currentDomain } = useMemo(() => splitEmail(currentEmail), [currentEmail]);
  const suggestedFullEmail = local ? `${local}@${domain}` : `you@${domain}`;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      trackEvent("Signup: Email Domain Modal Opened", {
        page: "Signup: Email Domain Modal",
        domain,
      });
    }
    // Note: we intentionally do NOT fire `onCancel` on dismiss. Dismissals
    // (X / overlay / Escape / Keep-my-email) only close the dialog visually;
    // the inline "Did you mean…?" hint must stay so an accidental close is
    // recoverable. The hint disappears naturally when the parent's
    // validateEmailDomain re-runs against a no-longer-typo'd email, or when
    // the user confirms the suggestion (onConfirm).
  };

  const handleConfirm = () => {
    trackEvent("Signup: Email Domain Modal Yes Button Clicked", {
      page: "Signup",
      domain,
    });
    setOpen(false);
    onConfirm(domain);
  };

  const handleCancel = () => {
    trackEvent("Signup: Email Domain Modal No Button Clicked", {
      page: "Signup",
      domain,
    });
    setOpen(false);
  };

  return (
    <>
      {/* Inline helper — whole line is the link */}
      <button
        type="button"
        onClick={() => handleOpenChange(true)}
        className="text-primary text-left text-xs leading-relaxed font-semibold underline-offset-2 transition-colors hover:underline focus:outline-hidden focus-visible:underline"
      >
        {t("signup_page.email_domain_popup.hint_prefix")} {suggestedFullEmail}
        {t("signup_page.email_domain_popup.hint_suffix")}
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="space-y-1.5 text-left">
            <DialogTitle className="text-lg font-semibold tracking-tight">
              {t("signup_page.email_domain_popup.dialog_title")}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm leading-relaxed">
              {t("signup_page.email_domain_popup.dialog_description")}
            </DialogDescription>
          </DialogHeader>

          {/* Typography-driven comparison — no boxes; changed portion highlighted */}
          <dl className="divide-border divide-y text-sm">
            {currentEmail && (
              <div className="flex items-baseline justify-between gap-4 py-2.5">
                <dt className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
                  {t("signup_page.email_domain_popup.your_email_label")}
                </dt>
                <dd className="font-mono break-all">
                  <span className="text-muted-foreground line-through">{local}@</span>
                  <span className="text-destructive line-through">{currentDomain}</span>
                </dd>
              </div>
            )}
            <div className="flex items-baseline justify-between gap-4 py-2.5">
              <dt className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
                {t("signup_page.email_domain_popup.suggested_email_label")}
              </dt>
              <dd className="font-mono font-medium break-all">
                <span className="text-foreground">{local || "you"}@</span>
                <span className="text-primary">{domain}</span>
              </dd>
            </div>
          </dl>

          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCancel}
              className="rounded-md text-sm"
            >
              {t("signup_page.email_domain_popup.cancel_button")}
            </Button>
            <Button type="button" onClick={handleConfirm} className="rounded-md text-sm">
              {t("signup_page.email_domain_popup.confirm_button")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmailDomainPopup;
